import { z } from 'zod';
import { RunStatusSchema, RunTriggerSchema } from './inventory.js';
export const FeedSchema = z.enum(['PRODUCTION', 'SCANNER']);
/** Telo `POST /wordfence/sync` — feed sa prijíma bez ohľadu na veľkosť písmen (`production` aj `PRODUCTION`). */
export const SyncFeedInputSchema = z.object({
    feed: z.preprocess((v) => (typeof v === 'string' ? v.toUpperCase() : v), FeedSchema),
});
/** Denný strop requestov, ktoré si dovolíme minúť (48 na kľúč, 4 ostávajú ako rezerva). */
export const WORDFENCE_DAILY_CAP = 44;
export const WORDFENCE_KEY_LIMIT = 48;
/** Jedno stiahnutie feedu. Míňa kvótu — nie je to ScanRun. */
export const FeedSyncRunViewSchema = z.object({
    id: z.string(),
    feed: FeedSchema,
    trigger: RunTriggerSchema,
    status: RunStatusSchema,
    startedAt: z.string(),
    finishedAt: z.string().nullable(),
    httpStatus: z.number().int().nullable(),
    bytes: z.number().nullable(),
    records: z.number().int().nullable(),
    changed: z.number().int().nullable(),
    error: z.string().nullable(),
});
/**
 * Prečo sa sync nedá spustiť:
 *   key_missing   — WORDFENCE_API_KEY nie je v prostredí
 *   breaker_open  — zlý kľúč alebo stiahnutý endpoint; resetuje človek
 *   window_closed — 30-min okno ešte nevypršalo (alebo Retry-After po 429)
 *   daily_cap     — dnešných 44 requestov je minutých
 */
export const QuotaRefusalKindSchema = z.enum(['key_missing', 'breaker_open', 'window_closed', 'daily_cap']);
/** Odpoveď 409 na `POST /wordfence/sync`, keď bol sync odmietnutý — slot sa neminul, HTTP sa nevolalo. */
export const SyncRefusedSchema = z.object({
    refused: z.literal(true),
    kind: QuotaRefusalKindSchema,
    message: z.string(),
    /** kedy sa otvorí ďalšie okno (window_closed, daily_cap) */
    opensAt: z.string().nullable(),
    remainingToday: z.number().int(),
    /** dôvod breakera (breaker_open) */
    reason: z.string().nullable(),
});
/** Stav jedného prijatého webhook eventu (WebhookEvent): uložený → spracovaný / zlyhal. */
export const WebhookEventStatusSchema = z.enum(['RECEIVED', 'PROCESSED', 'FAILED']);
export const WebhookEventViewSchema = z.object({
    id: z.string(),
    receivedAt: z.string(),
    processedAt: z.string().nullable(),
    status: WebhookEventStatusSchema,
    error: z.string().nullable(),
    /** koľko záznamov zraniteľností payload nesie (0 = nerozpoznaný tvar) */
    records: z.number().int(),
    /** titulok prvého záznamu — aby zoznam eventov hovoril, o čo išlo */
    title: z.string().nullable(),
});
/**
 * Čo aplikácia eventu urobila s katalógom:
 *   applied   — záznam zapísaný (nový alebo zmenený)
 *   stale     — payload je starší než uložený záznam → nezapísaný
 *   unchanged — rovnaký obsah (hash), len obnovená vlajka feedu
 *   rejected  — záznam sa nedal normalizovať (chýba id/title/slug…)
 */
export const WebhookApplyResultSchema = z.object({
    records: z.number().int(),
    applied: z.number().int(),
    stale: z.number().int(),
    unchanged: z.number().int(),
    rejected: z.number().int(),
});
/** Odpoveď `POST /wordfence/webhook/events/:id/replay`. */
export const WebhookReplayResultSchema = z.object({
    event: WebhookEventViewSchema,
    result: WebhookApplyResultSchema.nullable(),
});
/** Odpoveď 200 na `POST /wordfence/webhook` — event je uložený; duplicate = ten istý bajtový obsah už máme. */
export const WebhookAckSchema = z.object({
    received: z.literal(true),
    duplicate: z.boolean(),
    id: z.string(),
});
export const WebhookStatusSchema = z.object({
    /** WORDFENCE_WEBHOOK_SECRET je v prostredí — bez neho endpoint vracia 503 a nič neukladá */
    configured: z.boolean(),
    lastSeenAt: z.string().nullable(),
    /** uložené, ešte nespracované */
    pending: z.number().int(),
    failed: z.number().int(),
    processed: z.number().int(),
    /** posledných 10 eventov, najnovší prvý */
    events: z.array(WebhookEventViewSchema),
});
export const WordfenceStatusSchema = z.object({
    key: z.object({
        configured: z.boolean(),
        usedToday: z.number().int(),
        cap: z.number().int(),
        remainingToday: z.number().int(),
        windowOpensAt: z.string().nullable(),
        breakerOpen: z.boolean(),
        breakerReason: z.string().nullable(),
    }),
    /** null = sync sa dá spustiť; inak dôvod, prečo je tlačidlo zakázané */
    canSync: SyncRefusedSchema.omit({ refused: true }).nullable(),
    catalog: z.object({
        total: z.number().int(),
        production: z.number().int(),
        /** len v scanner feede — nepotvrdené (bez CVSS), nie neškodné */
        scannerOnly: z.number().int(),
        informational: z.number().int(),
        deleted: z.number().int(),
        ranges: z.number().int(),
    }),
    /** rozvrh cronu v UTC (HH:MM), aby Nastavenia nemuseli poznať cron výrazy */
    schedule: z.object({
        production: z.array(z.string()),
        scanner: z.array(z.string()),
    }),
    lastProductionSyncAt: z.string().nullable(),
    lastScannerSyncAt: z.string().nullable(),
    webhookLastSeenAt: z.string().nullable(),
    webhook: WebhookStatusSchema,
    runs: z.array(FeedSyncRunViewSchema),
});
